'use client';
import {
    createRef,
    useEffect,
    useRef,
    useMemo,
    useCallback,
    RefObject,
} from 'react';
import ReactImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import './index.css';
import Thumbnails from './Thumbnails';
import GalleryButtons from './GalleryButtons';
import { useAppDispatch, useAppSelector } from '@/hook/redux-hooks';
import {
    setActiveGalleryCategory,
    setActiveMainGalleryImage,
    setGalleryImages,
    setGalleryImagesCategories,
    setInnerModalFlag,
    setInnerModalType,
} from '@/redux/reducers/gallery.reducer';
import { Deg360, TriangleAlert } from '../../../../../public/Images';

import { useTranslation } from '@/hook';
import { NavLeft, NavRight } from '../../../../../public/Images';
import Typography from '@/components/shared/typography/typography.component';
import { ModalTypes } from '@/types/redux-state.types';
import NextImage from 'next/image';
import { useWindowDimensions } from '@/hook';
import myImageLoader from '@/image.local.loader';
import dynamic from 'next/dynamic';
import { CarTypesEnum } from '../../car-type';

const CarImagesModal = dynamic(
    () => import('./CarImagesModal').then(mod => mod.default),
    { ssr: false }
);

export interface GalleryBucket {
    bucket: string;
    images: string[];
}

export const Gallery = ({
    buckets,
    fireEvent,
    images,
    imperfections,
    interior3d,
    shouldRenderModals,
    className,
    altImageText,
    carType,
}: {
    buckets: GalleryBucket[];
    fireEvent: () => void;
    images?: any;
    imperfections: any;
    interior3d: string;
    shouldRenderModals: boolean;
    className?: string;
    altImageText?: string;
    carType: CarTypesEnum;
}) => {
    const { t, IS_RTL } = useTranslation();
    const { IS_DESKTOP } = useWindowDimensions();
    const dispatch = useAppDispatch();

    const { galleryImages, activeMainGalleryImage } = useAppSelector(
        state => state.gallery
    );

    const galleryRef = useRef<any>(null);
    const thumbnailsRefs = useRef<RefObject<HTMLElement>[]>([]);

    // Build items from buckets (unchanged)
    const reformatGalleryImagesImages = useCallback(() => {
        const allCategories = [
            'Exterior',
            'OpenDoors',
            'Interior',
            'Engine',
            'Tires',
            'Others',
        ];
        const categories: string[] = [];
        const imgs: ReactImageGalleryItem[] = [];

        allCategories.forEach(cat => {
            const idx = buckets?.findIndex(el => el.bucket === cat);
            if (idx !== -1 && buckets?.[idx]) {
                const { bucket, images } = buckets[idx];
                categories.push(bucket);
                images?.forEach((image: string) => {
                    imgs.push({
                        original: myImageLoader({
                            src: image,
                            height: IS_DESKTOP ? 800 : 400,
                            width: IS_DESKTOP ? 800 : 500,
                            quality: 90,
                        }),
                        thumbnail: myImageLoader({
                            src: image,
                            height: 111,
                            width: 148,
                            quality: 90,
                        }),
                        category: categories.findIndex(e => e === bucket),
                    } as any);
                });
            }
        });

        dispatch(setGalleryImagesCategories(categories));
        return imgs as any;
    }, [buckets, IS_DESKTOP, dispatch]);

    useEffect(() => {
        dispatch(setGalleryImages(reformatGalleryImagesImages()));
    }, [reformatGalleryImagesImages, dispatch]);

    useEffect(() => {
        if (!galleryImages?.length) {
            thumbnailsRefs.current = [];
            return;
        }
        thumbnailsRefs.current = galleryImages.map(() =>
            createRef<HTMLElement>()
        );
    }, [galleryImages]);

    // RTL fix: reverse items for the gallery; keep mapping helpers
    const galleryItems = useMemo(
        () =>
            IS_RTL ? [...(galleryImages || [])].reverse() : galleryImages || [],
        [IS_RTL, galleryImages]
    );

    const count = galleryItems.length;

    const toGalleryIndex = useCallback(
        (uiIndex: number) => (IS_RTL ? count - 1 - uiIndex : uiIndex),
        [IS_RTL, count]
    );

    const fromGalleryIndex = useCallback(
        (gIndex: number) => (IS_RTL ? count - 1 - gIndex : gIndex),
        [IS_RTL, count]
    );

    const canGoPrev = activeMainGalleryImage > 0;
    const canGoNext = activeMainGalleryImage < Math.max(0, count - 1);

    // Programmatic slide for nav/thumbs; let the lib animate
    const slideToGalleryIndex = useCallback(
        (gIndex: number) => {
            if (!galleryRef.current) return;
            const clamped = Math.max(0, Math.min(count - 1, gIndex));
            galleryRef.current.slideToIndex?.(clamped);
        },
        [count]
    );

    // Restore original feel: do state sync AFTER the animation step
    const onSlide = useCallback(
        (nextGalleryIndex: number) => {
            const nextUiIndex = fromGalleryIndex(nextGalleryIndex);
            dispatch(setActiveMainGalleryImage(nextUiIndex));
            // Light work only; defer heavier handlers
            requestIdleCallback?.(() => {
                try {
                    fireEvent();
                } catch {
                    /* empty */
                }
            });
            // Keep thumbnail in view (UI order)
            thumbnailsRefs.current[nextUiIndex]?.current?.scrollIntoView?.({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            } as ScrollIntoViewOptions);
        },
        [dispatch, fromGalleryIndex, fireEvent]
    );

    const onNavClicked = (direction: 'left' | 'right') => {
        const delta = direction === 'left' ? -1 : 1;
        const nextUi = Math.max(
            0,
            Math.min(count - 1, activeMainGalleryImage + delta)
        );
        const nextG = toGalleryIndex(nextUi);
        slideToGalleryIndex(nextG);
    };

    const onGalleryClick = () => {
        const currentUi = activeMainGalleryImage;
        // @ts-ignore category is present on our items in Redux copy
        dispatch(setActiveGalleryCategory(galleryImages[currentUi].category));
        dispatch(setInnerModalType(ModalTypes.Gallery));
        dispatch(setInnerModalFlag(true));
    };

    const setActiveImage = (uiIndex: number) => {
        const gIndex = toGalleryIndex(uiIndex);
        slideToGalleryIndex(gIndex);
    };

    const galleryKey = `${IS_RTL ? 'rtl' : 'ltr'}-${count}`;
    const startGalleryIndex = toGalleryIndex(activeMainGalleryImage);

    // Neighbor pre-decode (use window.Image to avoid NextImage collision)
    useEffect(() => {
        if (!count) return;
        const ui = activeMainGalleryImage;
        const ahead = [ui + 1, ui - 1].filter(i => i >= 0 && i < count);
        ahead.forEach(i => {
            const mapped = toGalleryIndex(i);
            const src = (galleryItems[mapped]?.original as string) || '';
            if (!src || typeof window === 'undefined' || !('Image' in window))
                return;
            const preload = new window.Image();
            (preload as any).decoding = 'async';
            preload.src = src;
        });
    }, [activeMainGalleryImage, galleryItems, count, toGalleryIndex]);

    return (
        <div className={`flex flex-col ${className ?? ''}`}>
            <div className="md:min-h-[60vh]">
                {/*// @ts-ignore*/}
                <ReactImageGallery
                    key={galleryKey}
                    ref={galleryRef}
                    items={galleryItems}
                    showPlayButton={false}
                    useBrowserFullscreen={false}
                    showNav
                    startIndex={startGalleryIndex}
                    additionalClass="app-image-gallery"
                    onClick={onGalleryClick}
                    onSlide={onSlide}
                    showFullscreenButton={false}
                    disableKeyDown
                    disableSwipe={false}
                    stopPropagation
                    infinite={false}
                    showThumbnails={false}
                    renderItem={(item: ReactImageGalleryItem) => (
                        <div className="h-[250px] md:h-[520px] flex items-center justify-center">
                            <img
                                alt={altImageText || 'Active gallery image'}
                                src={item.original as string}
                                className="max-h-full w-auto object-contain active-image"
                                decoding="async"
                            />
                        </div>
                    )}
                    // Must accept (onClick, disabled)
                    renderLeftNav={(
                        onClick: React.MouseEventHandler<HTMLElement>,
                        disabled: boolean
                    ) =>
                        canGoPrev ? (
                            <div>
                                <div
                                    className="navBtn"
                                    style={{ left: '3%' }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onNavClicked('left');
                                        onClick?.(e);
                                    }}
                                    aria-disabled={disabled}
                                >
                                    <NextImage
                                        src={NavLeft}
                                        width={7}
                                        height={14}
                                        alt="NavLeft"
                                    />
                                </div>
                                <div className="md:hidden">
                                    <GalleryMobileButtons
                                        imperfections={imperfections}
                                        carType={carType}
                                    />
                                </div>
                            </div>
                        ) : null
                    }
                    renderRightNav={(
                        onClick: React.MouseEventHandler<HTMLElement>,
                        disabled: boolean
                    ) =>
                        canGoNext ? (
                            <div>
                                <div
                                    className="navBtn"
                                    style={{ right: '3%' }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onNavClicked('right');
                                        onClick?.(e);
                                    }}
                                    aria-disabled={disabled}
                                >
                                    <NextImage
                                        src={NavRight}
                                        width={7}
                                        height={14}
                                        alt="NavRight"
                                    />
                                </div>
                                <div className="md:hidden">
                                    <GalleryMobileButtons
                                        imperfections={imperfections}
                                        carType={carType}
                                    />
                                </div>
                            </div>
                        ) : null
                    }
                    renderCustomControls={() => (
                        <Typography
                            variant="captionMedium"
                            className="bg-black/60 px-2 py-1 rounded-md absolute z-10 text-white bottom-4 ar:left-4 en:right-4"
                        >
                            {t('imgsCount', {
                                index: activeMainGalleryImage + 1,
                                count,
                            })}
                        </Typography>
                    )}
                />

                <div className="max-md:hidden">
                    <Thumbnails
                        images={galleryItems}
                        activeImage={toGalleryIndex(activeMainGalleryImage)}
                        setActiveImage={(mapped: number) =>
                            setActiveImage(fromGalleryIndex(mapped))
                        }
                        fireEvent={fireEvent}
                        refs={thumbnailsRefs}
                        containerClass="thumbnails-container"
                    />
                </div>
            </div>

            <div className="mt-auto mb-0 max-md:hidden">
                <GalleryButtons
                    fireEvent={fireEvent}
                    images={images}
                    carType={carType}
                />
                {shouldRenderModals && (
                    <CarImagesModal
                        images={images}
                        imperfections={imperfections}
                        interior3d={interior3d}
                        carType={carType}
                    />
                )}
            </div>
        </div>
    );
};

const GalleryMobileButtons = ({
    imperfections,
    carType,
}: {
    imperfections: any;
    carType: CarTypesEnum;
}) => {
    const dispatch = useAppDispatch();
    const isDealerCar = carType === CarTypesEnum.Dealer;
    if (isDealerCar) return;
    return (
        <div className="absolute top-4 z-[2] flex flex-col gap-4 ar:right-4 en:left-4">
            <button
                className="flex flex-col justify-center items-center rounded-full shrink-0 p-2"
                onClick={() => {
                    dispatch(setInnerModalType(ModalTypes.View360));
                    dispatch(setInnerModalFlag(true));
                }}
                style={{
                    background:
                        'linear-gradient(218deg, rgba(255, 255, 255, 0.60) 12.83%, rgba(255, 255, 255, 0.00) 61.59%), #FAC515',
                }}
            >
                <NextImage src={Deg360} alt="Deg360" width={28} height={28} />
            </button>
            {imperfections.length > 0 ? (
                <button
                    className="relative flex flex-col justify-center items-center rounded-full shrink-0 p-2 bg-white w-11 h-11"
                    onClick={() => {
                        dispatch(setInnerModalType(ModalTypes.Imperfections));
                        dispatch(setInnerModalFlag(true));
                    }}
                >
                    <NextImage
                        src={TriangleAlert}
                        alt="Deg360"
                        width={18}
                        height={18}
                    />
                    {!!imperfections.length && (
                        <div className="text-white flex h-5 w-5 justify-center items-center absolute -top-1 rounded-full bg-warning-600 ar:-left-1 en:-right-1">
                            <Typography
                                variant="labelMedium"
                                style={{
                                    fontWeight: 600,
                                    fontSize: '0.675rem',
                                }}
                            >
                                {imperfections.length}
                            </Typography>
                        </div>
                    )}
                </button>
            ) : null}
        </div>
    );
};
